<svelte:options
  customElement={{
    tag: "oc-dropdown-v1",
    shadow: "none",
    /*                                                         */
    /*         */
    extend: (customElementConstructor) => {
      return class extends customElementConstructor {
        /*                                          */
        static formAssociated = true;

        constructor() {
          super();
          this.attachShadow({ mode: "open", delegatesFocus: true });

          /*                             */
          this.hostElement = this;

          /*                                */
          this.internals = this.attachInternals();

          this.setValidity = this.internals.setValidity.bind(this.internals);
        }

        /*                                                            */
        get willValidate() {
          return this.internals.willValidate;
        }

        get validity() {
          return this.internals.validity;
        }

        get validationMessage() {
          return this.internals.validationMessage;
        }

        get form() {
          return this.internals.form;
        }

        /*                       */
        formResetCallback() {
          this.value = this.getAttribute("value") || "";
        }
      };
    },
    props: {
      aria: { type: "Object" },
      label: { type: "String" },
      name: { type: "String", reflect: true }, /*                                    */
      value: { type: "String" }, /*                                     */
      options: { type: "Array" },
      hint: { type: "String" },
      variant: { type: "String" },
      required: { type: "Boolean" },
      disabled: { type: "Boolean", reflect: true }, /*                                    */
    },
  }}
/>

<script lang="ts">
  import { onMount } from "svelte";
  import HintAndCounterV1 from "../../../../common/components/HintAndCounterV1.svelte";
  import { usePropertyChange } from "../../../../common/utils/usePropertyChange";
  import type { Props } from "./DropdownV1.types";
  import type { CommonProps } from "../../../../common/component.types.js";
  import { useAriaAttributes } from "../../../../common/utils/aria/useAriaAttributes.js";
  import { applyAttributesToHost } from "../../../../common/utils/aria/applyAttributesToHost.js";

  usePropertyChange();

  export let aria: Required<CommonProps>["aria"] = {};

  $: ariaAttributes = useAriaAttributes(aria, { host: ["hidden"] });
  $: applyAttributesToHost(ariaAttributes.host);

  export let label: Props["label"] = undefined;
  export let name: Props["name"] = undefined;
  export let variant: Props["variant"] = "default";
  export let hint: Props["hint"] = undefined;
  export let value: Exclude<Props["value"], undefined> = "";
  export let required: Props["required"] = false;
  export let disabled: Props["disabled"] = false;
  export let options: Exclude<Props["options"], undefined> = [];

  export let hostElement: HTMLElement;

  export let internals: ElementInternals;

  let selectElement: HTMLSelectElement;

  onMount(() => {
    if (hostElement?.children) {
      /*                                                         */
      /*         */
      selectElement.append(...hostElement.children);
    }
  });

  /*                                      */
  $: if (selectElement) {
    internals.setFormValue(value);
    selectElement.value = value;
    if (!internals?.form?.noValidate) {
      internals.setValidity(selectElement.validity, selectElement.validationMessage, selectElement);
    }
  }

  $: {
    /*                                       */
    aria.describedby = hint ? "hint" : undefined;
  }

  function onChange() {
    value = selectElement.value;
  }
</script>

<div
  class="dropdown"
  class:dropdown--disabled={disabled}
  class:dropdown--error={variant === "error"}
  class:dropdown--selected={value}
>
  {#if label}
    <label for="dropdown" class="dropdown__label">{label}</label>
  {/if}

  <select
    {...ariaAttributes.default}
    bind:this={selectElement}
    class="dropdown__select"
    {disabled}
    id="dropdown"
    on:change={onChange}
    {required}
  >
    {#if label}
      <option value="" aria-label="keine Auswahl">{label}</option>
    {/if}

    <!-- eslint-disable-next-line no-shadow-->
    {#each options as { label, value }}
      <option {value}>{label}</option>
    {/each}
  </select>

  <div class="dropdown__arrow"></div>

  <HintAndCounterV1 {disabled} {hint} hintId="hint" {variant} />
</div>

<style lang="scss">
  @use "@otto-ec/design-tokens/tokens";
  @use "./DropdownV1.config" as config;

  :host {
    @include config.oc-dropdown;
    overflow: initial !important; /*            */
  }

  :host[hint] {
    @include config.oc-dropdown-hint;
  }

  .dropdown {
    box-sizing: border-box;
    position: relative;

    &__select {
      all: unset;
      width: 100%;
      display: inline-block;
      box-sizing: border-box;
      padding: tokens.$oc-component-form-field-padding-y 2rem
        tokens.$oc-component-form-field-padding-y tokens.$oc-component-form-field-padding-x;
      font: tokens.$oc-component-form-field-input-font;
      color: tokens.$oc-component-form-field-input-color;
      text-overflow: ellipsis;
      white-space: nowrap;
      background: tokens.$oc-component-form-background-color;
      outline: 1px solid tokens.$oc-component-form-default-border-color;
      border-radius: tokens.$oc-component-form-field-border-radius;

      &:hover {
        outline: 2px solid tokens.$oc-component-form-default-border-color;
      }

      &:focus {
        outline: 2px solid tokens.$oc-component-form-default-border-color-focus;
      }
    }

    &__arrow {
      position: absolute;
      top: 15px;
      right: 12px;
      width: 0.75rem;
      height: 0.75rem;
      margin-top: 0.25rem; /*                                                  */
      mask-image: url("/assets-static/icons/pl_icon_arrow-down50.svg");
      background-color: tokens.$oc-component-form-default-icon-color;
      pointer-events: none;
    }

    &__label {
      position: absolute;
      top: 0;
      left: 0;
      padding: tokens.$oc-component-form-field-padding-y 2rem
        tokens.$oc-component-form-field-padding-y tokens.$oc-component-form-field-padding-x;
      max-width: 100%;
      box-sizing: border-box;
      font: tokens.$oc-component-form-label-font;
      color: tokens.$oc-component-form-default-label-color;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      user-select: none;
      pointer-events: none;
      background-color: tokens.$oc-component-form-background-color;
      border-radius: tokens.$oc-component-form-field-border-radius;
      transition: all 0.1s cubic-bezier(0.4, 0, 0.2, 1);
    }

    &--selected {
      .dropdown__label {
        top: -0.5rem;
        left: tokens.$oc-component-form-field-floating-label-spacing-left;
        padding: 0 tokens.$oc-component-form-field-floating-label-inner-spacing-x;
        font: tokens.$oc-component-form-field-floating-label-font;
        border-radius: tokens.$oc-component-form-field-label-border-radius;
      }
    }

    &--error {
      .dropdown__label {
        color: tokens.$oc-component-form-error-label-color;
      }

      .dropdown__select {
        color: tokens.$oc-component-form-error-label-color;
        outline-color: tokens.$oc-component-form-error-border-color;

        &:hover {
          outline-color: tokens.$oc-component-form-error-border-color;
        }

        &:focus {
          outline-color: tokens.$oc-component-form-error-border-color;
        }
      }

      .dropdown__arrow {
        background-color: tokens.$oc-component-form-error-icon-color;
      }
    }

    &--disabled {
      .dropdown__label {
        color: tokens.$oc-component-form-disabled-label-color;
        border-color: tokens.$oc-component-form-disabled-border-color;
        background-color: tokens.$oc-component-form-disabled-background-color;
      }

      .dropdown__select {
        color: tokens.$oc-component-form-disabled-label-color;
        outline-color: tokens.$oc-component-form-disabled-border-color;
        background-color: tokens.$oc-component-form-disabled-background-color;

        &:hover {
          outline: 1px solid tokens.$oc-component-form-disabled-border-color;
        }
      }

      .dropdown__arrow {
        background-color: tokens.$oc-component-form-disabled-icon-color;
      }
    }
  }
</style>
